import { memo, useEffect, useRef, useState } from 'react';
import { IconCircleCheck } from '@tabler/icons-react';
import Hint from '@/components/common/Hint';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import BiggaponTemplate, { TEMPLATES, THEME_GROUPS } from './templates';

// One theme as a thumbnail of the REAL poster (rendered from a sample job), so the choice stays visual. Memoised: the posters
// only redraw when the theme, the sample job or the shop footer changes — not on every keystroke in the editor next to them.
// The button is named by the theme (aria-label); the poster inside is only a picture, so a screen reader doesn't read it out.
const ThemeThumb = memo(function ThemeThumb({ tpl, selected, onPick, sampleJob, business }) {
    // Every thumbnail is a whole poster that fits its type to the sheet when it mounts — drawing all of them when the window opens would
    // freeze it for seconds. A coloured placeholder of the same shape stands in until the thumbnail is near the viewport.
    const holder = useRef(null);
    const [near, setNear] = useState(false);
    useEffect(() => {
        const el = holder.current;
        if (near || !el) return undefined;
        if (typeof IntersectionObserver === 'undefined') { setNear(true); return undefined; }
        const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { setNear(true); io.disconnect(); } }, { rootMargin: '240px 0px' });
        io.observe(el);
        return () => io.disconnect();
    }, [near]);
    return (
        <Hint label={tpl.name} side="top">
            <button
                type="button"
                onClick={() => onPick(tpl.id)}
                aria-label={tpl.name}
                aria-pressed={selected}
                className="group flex h-full flex-col items-center justify-center gap-0.5"
            >
                <div
                    ref={holder}
                    aria-hidden="true"
                    className={`relative w-full overflow-hidden rounded-md border-2 transition ${
                        selected ? 'border-primary ring-2 ring-primary/40' : 'border-zinc-200 dark:border-zinc-700 group-hover:border-primary/50'
                    }`}
                >
                    {sampleJob && near ? (
                        <BiggaponTemplate templateId={tpl.id} job={sampleJob} business={business} showFooter />
                    ) : (
                        <div className="w-full" style={{ background: tpl.posterBg, aspectRatio: tpl.orientation === 'landscape' ? '297 / 210' : '210 / 297' }} />
                    )}
                    {selected && <IconCircleCheck size={15} className="absolute right-1 top-1 rounded-full bg-white text-primary dark:bg-zinc-900" />}
                </div>
                <span className={`w-full truncate text-center text-[10px] leading-tight ${selected ? 'font-semibold text-primary' : 'text-zinc-500 dark:text-zinc-400'}`}>
                    {tpl.name}
                </span>
            </button>
        </Hint>
    );
});

/**
 * BiggaponStyleGrid — every poster theme (TEMPLATES) as grids of real-poster thumbnails, under a heading per kind (the new designs, the wide
 * ones, the classic layouts). `onChange(themeId)` must be a stable function (a store setter) so the thumbnails don't redraw needlessly.
 */
export default function BiggaponStyleGrid({ value, onChange, sampleJob, business }) {
    const { t } = useTranslation();
    return (
        <div className="flex flex-col gap-4">
            {THEME_GROUPS.map((group) => {
                const items = TEMPLATES.filter((tpl) => tpl.group === group);
                if (!items.length) return null;
                return (
                    <div key={group} className="flex flex-col gap-2">
                        <div className="text-[11px] font-semibold text-zinc-500 dark:text-zinc-400">{t(`biggapon.stylePicker.groups.${group}`)}</div>
                        <div className="grid auto-rows-fr grid-cols-3 content-start gap-2.5">
                            {items.map((tpl) => (
                                <ThemeThumb key={tpl.id} tpl={tpl} selected={value === tpl.id} onPick={onChange} sampleJob={sampleJob} business={business} />
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
